import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Search, Filter, RefreshCw, FileText } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Card, CardContent } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Skeleton } from "@shared/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { ticketService } from "@features/tickets/services/ticketService";
import TicketCard from "./TicketCard";
import CreateTicketDialog from "./CreateTicketDialog";
import TicketFilters from "./TicketFilters";

const TicketsList = () => {
  const { t } = useTranslation();
  const { user, isAdmin, isManager } = useAuth();
  const queryClient = useQueryClient();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    sort: "createdAt",
    order: "desc",
  });

  // Determine which endpoint to use based on user role
  const isManagerOrAdmin = isAdmin || isManager;
  const ticketsEndpoint = isManagerOrAdmin ? "getAllTickets" : "getMyTickets";

  const {
    data: ticketsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tickets", ticketsEndpoint, filters, activeTab],
    queryFn: () => {
      const ticketFilters = { ...filters };
      if (searchTerm.trim()) {
        ticketFilters.search = searchTerm.trim();
      }
      // Apply status filter based on active tab
      if (activeTab !== "all") {
        ticketFilters.status = activeTab;
      }

      return isManagerOrAdmin
        ? ticketService.getAllTickets(ticketFilters)
        : ticketService.getMyTickets(ticketFilters);
    },
    refetchOnWindowFocus: false,
  });

  // Get statistics for admin/manager
  const { data: statisticsData } = useQuery({
    queryKey: ["ticketStatistics"],
    queryFn: () => ticketService.getTicketStatistics(),
    enabled: isManagerOrAdmin,
    refetchOnWindowFocus: false,
  });

  const handleSearch = (value) => {
    setSearchTerm(value);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleTicketCreated = () => {
    setShowCreateDialog(false);
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
    if (isManagerOrAdmin) {
      queryClient.invalidateQueries({ queryKey: ["ticketStatistics"] });
    }
  };

  const tickets = ticketsData?.data || [];
  const pageInfo = ticketsData?.pageInfo || {};

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("tickets.errorLoading", "Error loading tickets")}
            </h3>
            <p className="text-gray-500 mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("common.retry", "Retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isManagerOrAdmin
              ? t("tickets.allTickets", "All Tickets")
              : t("tickets.myTickets", "My Tickets")}
          </h1>
          <p className="text-gray-500 mt-1">
            {isManagerOrAdmin
              ? t(
                  "tickets.manageAllTickets",
                  "Manage and respond to all support tickets"
                )
              : t(
                  "tickets.viewMyTickets",
                  "View and manage your support requests"
                )}
          </p>
        </div>

        {(user?.role === "client" || user?.role === "livreur") && (
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("tickets.createTicket", "Create Ticket")}
          </Button>
        )}
      </div>

      {/* Statistics Cards for Admin/Manager */}
      {isManagerOrAdmin && statisticsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                {statisticsData.data.overview.totalTickets}
              </div>
              <div className="text-sm text-gray-500">
                {t("tickets.totalTickets", "Total Tickets")}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-600">
                {statisticsData.data.overview.openTickets}
              </div>
              <div className="text-sm text-gray-500">
                {t("tickets.openTickets", "Open Tickets")}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-600">
                {statisticsData.data.overview.inProgressTickets}
              </div>
              <div className="text-sm text-gray-500">
                {t("tickets.inProgressTickets", "In Progress")}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">
                {statisticsData.data.overview.urgentTickets}
              </div>
              <div className="text-sm text-gray-500">
                {t("tickets.urgentTickets", "Urgent")}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">
                {statisticsData.data.overview.resolvedTickets}
              </div>
              <div className="text-sm text-gray-500">
                {t("tickets.resolvedTickets", "Resolved")}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Status Filtering */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
          <TabsTrigger value="all" className="flex items-center gap-2">
            {t("tickets.allTickets", "All")}
            {statisticsData && (
              <Badge variant="secondary" className="ml-1">
                {statisticsData.data.overview.totalTickets}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="open" className="flex items-center gap-2">
            {t("tickets.status.open", "Open")}
            {statisticsData && (
              <Badge variant="secondary" className="ml-1">
                {statisticsData.data.overview.openTickets}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="in_progress" className="flex items-center gap-2">
            {t("tickets.status.in_progress", "In Progress")}
            {statisticsData && (
              <Badge variant="secondary" className="ml-1">
                {statisticsData.data.overview.inProgressTickets}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="waiting_for_response"
            className="flex items-center gap-2"
          >
            {t("tickets.status.waiting_for_response", "Waiting")}
          </TabsTrigger>
          <TabsTrigger value="resolved" className="flex items-center gap-2">
            {t("tickets.status.resolved", "Resolved")}
            {statisticsData && (
              <Badge variant="secondary" className="ml-1">
                {statisticsData.data.overview.resolvedTickets}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="closed" className="flex items-center gap-2">
            {t("tickets.status.closed", "Closed")}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Tickets List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || Object.keys(filters).length > 4
                ? t("tickets.noTicketsFound", "No tickets found")
                : t("tickets.noTicketsYet", "No tickets yet")}
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              {searchTerm || Object.keys(filters).length > 4
                ? t(
                    "tickets.tryDifferentSearch",
                    "Try adjusting your search or filters"
                  )
                : isManagerOrAdmin
                ? t(
                    "tickets.noTicketsForManagement",
                    "No support tickets have been created yet"
                  )
                : t(
                    "tickets.createFirstTicket",
                    "Create your first support ticket to get help"
                  )}
            </p>
            {!isManagerOrAdmin && !searchTerm && (
              <Button
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("tickets.createTicket", "Create Ticket")}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <TicketCard
              key={ticket._id}
              ticket={ticket}
              isManagerOrAdmin={isManagerOrAdmin}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pageInfo.totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageInfo.currentPage === 1}
            onClick={() => handlePageChange(pageInfo.currentPage - 1)}
          >
            {t("common.previous", "Previous")}
          </Button>

          <span className="px-4 py-2 text-sm text-gray-600">
            {t("common.pageOfTotal", "Page {{current}} of {{total}}", {
              current: pageInfo.currentPage,
              total: pageInfo.totalPages,
            })}
          </span>

          <Button
            variant="outline"
            size="sm"
            disabled={pageInfo.currentPage === pageInfo.totalPages}
            onClick={() => handlePageChange(pageInfo.currentPage + 1)}
          >
            {t("common.next", "Next")}
          </Button>
        </div>
      )}

      {/* Create Ticket Dialog */}
      {showCreateDialog && (
        <CreateTicketDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={handleTicketCreated}
        />
      )}
    </div>
  );
};

export default TicketsList;
